import { LayerCache } from "./pagecache";
import { bayer, css, lum, mix, pack, u32 } from "../shared/color";
import { BACKGROUND, CELL, DEBRIS, EMPTY, SOLID } from "../shared/defs";
import { TerrainLog, type TerrainSink } from "./terrain";
import type { BlockRec, BoxPaint, DrawImage, Grad, ImgPaint, Level, Paint, Rect } from "../shared/types";

export type CarveCell = (x: number, y: number, color: number, mat: number, owner: number) => void;

export interface LiveBlock extends BlockRec {
  cells: number;
  lost: number;
  damage: number;
  hp: number;
  alive: boolean;
  shake: number;
  cracked: number;
  children: number[];
}

export type Ops = TerrainSink;

function roundPath(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, radius: number[]) {
  if (!(w > 0 && h > 0)) return;
  const cap = Math.min(w, h) / 2;
  const r = radius.map((n) => (n > 0 ? Math.min(n, cap) : 0));
  const [tl, tr, br, bl] = r;
  ctx.moveTo(x + tl, y);
  ctx.lineTo(x + w - tr, y);
  if (tr) ctx.arcTo(x + w, y, x + w, y + tr, tr);
  ctx.lineTo(x + w, y + h - br);
  if (br) ctx.arcTo(x + w, y + h, x + w - br, y + h, br);
  ctx.lineTo(x + bl, y + h);
  if (bl) ctx.arcTo(x, y + h, x, y + h - bl, bl);
  ctx.lineTo(x, y + tl);
  if (tl) ctx.arcTo(x, y, x + tl, y, tl);
  ctx.closePath();
}

function gradient(ctx: CanvasRenderingContext2D, grad: Grad, x: number, y: number, w: number, h: number) {
  const cx = x + w / 2;
  const cy = y + h / 2;
  let g: CanvasGradient;
  if (grad.type === "linear") {
    const dx = Math.sin(grad.angle);
    const dy = -Math.cos(grad.angle);
    const len = Math.abs(w * dx) + Math.abs(h * dy);
    g = ctx.createLinearGradient(cx - dx * len / 2, cy - dy * len / 2, cx + dx * len / 2, cy + dy * len / 2);
  } else g = ctx.createRadialGradient(cx, cy, 0, cx, cy, Math.hypot(w, h) / 2);
  for (const stop of grad.stops) g.addColorStop(Math.min(1, Math.max(0, stop.pos)), css(stop.color));
  return g;
}

function paintBox(ctx: CanvasRenderingContext2D, paint: BoxPaint, z: number) {
  ctx.save();
  ctx.setTransform(z, 0, 0, z, 0, 0);
  if (paint.clip) {
    ctx.beginPath();
    ctx.rect(paint.clip.x, paint.clip.y, paint.clip.w, paint.clip.h);
    ctx.clip();
  }
  ctx.globalAlpha = paint.opacity;
  if (paint.bg !== null || paint.grads.length || paint.images.length) {
    for (const shadow of paint.shadows) {
      if (!(paint.w + shadow.spread * 2 > 0 && paint.h + shadow.spread * 2 > 0)) continue;
      ctx.save();
      const shift = 20000;
      ctx.shadowColor = css(shadow.color);
      ctx.shadowBlur = shadow.blur * z;
      ctx.shadowOffsetX = (shadow.x + shift) * z;
      ctx.shadowOffsetY = shadow.y * z;
      ctx.fillStyle = "#000";
      ctx.beginPath();
      roundPath(ctx, paint.x - shadow.spread - shift, paint.y - shadow.spread, paint.w + shadow.spread * 2, paint.h + shadow.spread * 2, paint.radius.map((r) => r + shadow.spread));
      ctx.fill();
      ctx.restore();
    }
  }
  ctx.beginPath();
  roundPath(ctx, paint.x, paint.y, paint.w, paint.h, paint.radius);
  if (paint.bg !== null) {
    ctx.fillStyle = css(paint.bg);
    ctx.fill();
  }
  for (let i = paint.grads.length - 1; i >= 0; i--) {
    ctx.fillStyle = gradient(ctx, paint.grads[i], paint.x, paint.y, paint.w, paint.h);
    ctx.fill();
  }
  const images = paint.images.filter((img): img is DrawImage => "src" in img && !!img.src);
  if (images.length) {
    ctx.save();
    ctx.clip();
    for (const img of images) {
      if (img.dw < 1 || img.dh < 1) continue;
      ctx.imageSmoothingEnabled = !img.pixelated;
      let n = 0;
      const x0 = img.repeatX ? paint.x + ((((img.dx - paint.x) % img.dw) + img.dw) % img.dw) - img.dw : img.dx;
      const y0 = img.repeatY ? paint.y + ((((img.dy - paint.y) % img.dh) + img.dh) % img.dh) - img.dh : img.dy;
      for (let y = y0; y < paint.y + paint.h && n < 400; y += img.dh) {
        for (let x = x0; x < paint.x + paint.w && n < 400; x += img.dw) {
          try {
            ctx.drawImage(img.src, x, y, img.dw, img.dh);
          } catch {
            /* tainted or broken */
          }
          n++;
          if (!img.repeatX) break;
        }
        if (!img.repeatY) break;
      }
    }
    ctx.restore();
  }
  if (paint.border) {
    const [top, right, bottom, left] = paint.border.w;
    const [tc, rc, bc, lc] = paint.border.c;
    if (top === right && top === bottom && top === left && tc === rc && tc === bc && tc === lc && top > 0) {
      ctx.beginPath();
      roundPath(ctx, paint.x, paint.y, paint.w, paint.h, paint.radius);
      roundPath(ctx, paint.x + top, paint.y + top, paint.w - top * 2, paint.h - top * 2, paint.radius.map((r) => Math.max(0, r - top)));
      ctx.fillStyle = css(tc);
      ctx.fill("evenodd");
    } else {
      if (top > 0) {
        ctx.fillStyle = css(tc);
        ctx.fillRect(paint.x, paint.y, paint.w, top);
      }
      if (bottom > 0) {
        ctx.fillStyle = css(bc);
        ctx.fillRect(paint.x, paint.y + paint.h - bottom, paint.w, bottom);
      }
      if (left > 0) {
        ctx.fillStyle = css(lc);
        ctx.fillRect(paint.x, paint.y, left, paint.h);
      }
      if (right > 0) {
        ctx.fillStyle = css(rc);
        ctx.fillRect(paint.x + paint.w - right, paint.y, right, paint.h);
      }
    }
  }
  ctx.restore();
}

function radiusOf(radius: number[] | number): number[] {
  return typeof radius === "number" ? [radius, radius, radius, radius] : radius;
}

function paintImage(ctx: CanvasRenderingContext2D, paint: ImgPaint, z: number) {
  ctx.save();
  ctx.setTransform(z, 0, 0, z, 0, 0);
  ctx.beginPath();
  if (paint.clip) {
    ctx.rect(paint.clip.x, paint.clip.y, paint.clip.w, paint.clip.h);
    ctx.clip();
    ctx.beginPath();
  }
  roundPath(ctx, paint.x, paint.y, paint.w, paint.h, radiusOf(paint.radius));
  ctx.clip();
  ctx.globalAlpha = paint.opacity;
  ctx.imageSmoothingEnabled = !paint.pixelated;
  ctx.imageSmoothingQuality = "high";
  try {
    if (typeof paint.src !== "string") ctx.drawImage(paint.src, paint.dx, paint.dy, paint.dw, paint.dh);
    else {
      ctx.fillStyle = "#c9c9d1";
      ctx.fillRect(paint.x, paint.y, paint.w, paint.h);
    }
  } catch {
    ctx.fillStyle = "#c9c9d1";
    ctx.fillRect(paint.x, paint.y, paint.w, paint.h);
  }
  ctx.restore();
}

export function paintGlyph(ctx: CanvasRenderingContext2D, glyph: Level["glyphs"][number], z: number, ox = 0, oy = 0) {
  ctx.setTransform(z, 0, 0, z, 0, 0);
  ctx.font = glyph.font;
  ctx.fillStyle = css(glyph.color);
  ctx.textBaseline = "alphabetic";
  ctx.fillText(glyph.ch, glyph.x - ox, glyph.base - oy);
  if (glyph.underline) {
    const h = Math.max(1, glyph.size / 15);
    ctx.fillRect(glyph.x - ox, glyph.base - oy + Math.max(1.5, glyph.size * 0.11), glyph.w, h);
  }
}

function mk(w: number, h: number): HTMLCanvasElement {
  const c = document.createElement("canvas");
  c.width = Math.max(1, Math.floor(w));
  c.height = Math.max(1, Math.floor(h));
  return c;
}

export class World {
  W: number;
  H: number;
  GW: number;
  GH: number;
  Z: number;
  pix: number;
  mat: Uint8Array;
  col: Uint32Array;
  owner: Int32Array;
  lcount: Uint8Array;
  blocks: LiveBlock[] = [];
  base: number;
  totalCells: number;
  removedCells = 0;
  page: HTMLCanvasElement;
  pg: CanvasRenderingContext2D;
  text: HTMLCanvasElement;
  tx: CanvasRenderingContext2D;
  log: TerrainLog | null = null;
  /** Journey reserves the exit link. Cells here stay in the page until the portal opens. */
  keep: Rect | null = null;
  /** Called when a block's HP or lost-cell ratio crosses the detach line. */
  breaker: ((index: number, vx: number, vy: number, av: number) => void) | null = null;
  /** Crack marks stay off the page canvas so every client hashes the same pixels. */
  cracks: number[] = [];
  pageCache!: LayerCache;
  textCache!: LayerCache;
  private crackSet = new Set<number>();
  carveGroup = 1;
  carved: number[] = [];

  constructor(level: Level, z: number) {
    this.W = level.width;
    this.H = level.height;
    this.GW = Math.ceil(this.W / CELL);
    this.GH = Math.ceil(this.H / CELL);
    this.pix = Math.max(1, Math.round(CELL * z));
    this.Z = this.pix / CELL;
    this.base = level.base >>> 0;
    const n = this.GW * this.GH;
    this.mat = new Uint8Array(n).fill(BACKGROUND);
    this.col = new Uint32Array(n);
    this.owner = new Int32Array(n).fill(-1);
    this.lcount = new Uint8Array(n);
    this.totalCells = n;
    this.page = mk(this.GW * this.pix, this.GH * this.pix);
    this.text = mk(this.GW * this.pix, this.GH * this.pix);
    this.pg = this.page.getContext("2d", { willReadFrequently: true })!;
    this.tx = this.text.getContext("2d", { willReadFrequently: true })!;
    this.pg.fillStyle = css(this.base);
    this.pg.fillRect(0, 0, this.page.width, this.page.height);
    for (const paint of level.paints) {
      try {
        if (paint.k === "box") paintBox(this.pg, paint, this.Z);
        else paintImage(this.pg, paint, this.Z);
      } catch (err) {
        console.warn("[destroy-any-website] skipped a paint", err);
      }
    }
    this.pg.setTransform(1, 0, 0, 1, 0, 0);
    for (const glyph of level.glyphs) {
      try {
        paintGlyph(this.tx, glyph, this.Z);
      } catch {
        /* a bad font shorthand should not abort the level */
      }
    }
    this.tx.setTransform(1, 0, 0, 1, 0, 0);
    this.pageCache = new LayerCache(this.page, this.pg);
    this.textCache = new LayerCache(this.text, this.tx);
    const sample = mk(this.GW, this.GH);
    const sg = sample.getContext("2d", { willReadFrequently: true })!;
    sg.imageSmoothingEnabled = true;
    sg.imageSmoothingQuality = "high";
    sg.drawImage(this.page, 0, 0, this.GW, this.GH);
    const data = sg.getImageData(0, 0, this.GW, this.GH).data;
    for (let i = 0; i < n; i++) this.col[i] = pack(data[i * 4], data[i * 4 + 1], data[i * 4 + 2], 255);

    level.blocks.forEach((block, index) => {
      const x0 = Math.max(0, Math.floor(block.x / CELL));
      const y0 = Math.max(0, Math.floor(block.y / CELL));
      const x1 = Math.min(this.GW - 1, Math.ceil((block.x + block.w) / CELL) - 1);
      const y1 = Math.min(this.GH - 1, Math.ceil((block.y + block.h) / CELL) - 1);
      const live: LiveBlock = {
        ...block,
        post: block.post ?? index,
        canDetach: block.canDetach ?? true,
        cells: 0,
        lost: 0,
        damage: 0,
        hp: 20 + (block.kind === "image" ? 0.32 : 0.4) * Math.sqrt(block.w * block.h),
        alive: true,
        shake: 0,
        cracked: 0,
        children: [],
      };
      this.blocks.push(live);
      const rad = Math.min(block.radius, block.w / 2, block.h / 2);
      for (let y = y0; y <= y1; y++) {
        for (let x = x0; x <= x1; x++) {
          if (rad > 2) {
            const px = (x + 0.5) * CELL;
            const py = (y + 0.5) * CELL;
            const dx = Math.max(block.x + rad - px, px - (block.x + block.w - rad), 0);
            const dy = Math.max(block.y + rad - py, py - (block.y + block.h - rad), 0);
            if (dx * dx + dy * dy > rad * rad) continue;
          }
          const i = y * this.GW + x;
          this.mat[i] = SOLID;
          this.owner[i] = index;
        }
      }
    });
    for (let i = 0; i < n; i++) {
      const owner = this.owner[i];
      if (owner >= 0) this.blocks[owner].cells++;
    }
    this.blocks.forEach((block, index) => {
      if (block.parent >= 0 && this.blocks[block.parent]) this.blocks[block.parent].children.push(index);
    });
  }

  solid(x: number, y: number): boolean {
    if (y >= this.GH) return true;
    if (x < 0 || x >= this.GW || y < 0) return false;
    const i = y * this.GW + x;
    return this.mat[i] >= SOLID || this.lcount[i] > 0;
  }

  surface(x: number, y: number): boolean {
    const gw = this.GW;
    if (y === this.GH) return true;
    if (x < 0 || x >= gw || y < 0 || y > this.GH) return false;
    const i = y * gw + x;
    const top = y === 0;
    const above = i - gw;
    const letterAbove = !top && this.lcount[above] > 0;
    if (this.lcount[i] > 0 && !letterAbove) return true;
    const mat = this.mat[i];
    if (mat === DEBRIS) return top || (this.mat[above] !== DEBRIS && !letterAbove);
    if (mat !== SOLID) return false;
    if (top) return true;
    const up = this.mat[above];
    if (up === DEBRIS || letterAbove) return false;
    if (up !== SOLID) return true;
    const owner = this.owner[i];
    const ownerAbove = this.owner[above];
    if (ownerAbove === owner || owner < 0) return false;
    const post = this.blocks[owner]?.post ?? owner;
    return !(ownerAbove >= owner && ownerAbove <= post);
  }

  groundNear(x: number, y: number, up = 8, down = 12): number {
    return this.findSurface(x - 0.5, x + 0.5, y - up, y + down);
  }

  colorAt(x: number, y: number) {
    const col = Math.floor(x / CELL);
    const row = Math.floor(y / CELL);
    if (col < 0 || row < 0 || col >= this.GW || row >= this.GH) return this.base;
    return this.col[row * this.GW + col];
  }

  matAt(x: number, y: number) {
    const col = Math.floor(x / CELL);
    const row = Math.floor(y / CELL);
    if (col < 0 || row < 0 || col >= this.GW || row >= this.GH) return EMPTY;
    return this.mat[row * this.GW + col];
  }

  /** True when this cell is inside the reserved exit, so carving must leave it. */
  shields(col: number, row: number): boolean {
    const k = this.keep;
    if (!k || k.w <= 0 || k.h <= 0) return false;
    if (col < 0 || row < 0 || col >= this.GW || row >= this.GH) return false;
    const x = col * CELL;
    const y = row * CELL;
    return x < k.x + k.w && x + CELL > k.x && y < k.y + k.h && y + CELL > k.y;
  }

  /** Solid or debris cells overlapping a page-space box. */
  solidIn(rect: Rect): number {
    if (rect.w <= 0 || rect.h <= 0) return 0;
    const x0 = Math.max(0, Math.floor(rect.x / CELL));
    const y0 = Math.max(0, Math.floor(rect.y / CELL));
    const x1 = Math.min(this.GW - 1, Math.floor((rect.x + rect.w - 0.01) / CELL));
    const y1 = Math.min(this.GH - 1, Math.floor((rect.y + rect.h - 0.01) / CELL));
    let n = 0;
    for (let y = y0; y <= y1; y++) {
      const row = y * this.GW;
      for (let x = x0; x <= x1; x++) if (this.mat[row + x] !== EMPTY) n++;
    }
    return n;
  }

  findSurface(x0: number, x1: number, y0: number, y1: number): number {
    const c0 = Math.floor(x0 / CELL);
    const c1 = Math.floor(x1 / CELL);
    const r0 = Math.max(0, Math.ceil(y0 / CELL - 0.000001));
    const r1 = Math.min(this.GH, Math.floor(y1 / CELL + 0.000001));
    for (let y = r0; y <= r1; y++) {
      for (let x = c0; x <= c1; x++) if (this.surface(x, y)) return y * CELL;
    }
    return NaN;
  }

  beginCarve() {
    this.carveGroup = (this.carveGroup % 65000) + 1;
    this.carved.length = 0;
    return this.carveGroup;
  }

  take(x: number, y: number, on?: CarveCell): boolean {
    if (this.shields(x, y)) return false;
    if (x < 0 || y < 0 || x >= this.GW || y >= this.GH) return false;
    const i = y * this.GW + x;
    const mat = this.mat[i];
    if (mat === EMPTY) return false;
    const owner = this.owner[i];
    on?.(x, y, this.col[i], mat, owner);
    this.mat[i] = EMPTY;
    this.log?.clear(i);
    if (this.carved.length < 60000) this.carved.push(i);
    if (owner >= 0 && this.blocks[owner]) this.blocks[owner].lost++;
    this.removedCells++;
    return true;
  }

  carve(x: number, y: number, radius: number, rough = 0.12, on?: CarveCell): number {
    const rad = radius / CELL;
    const cx = x / CELL;
    const cy = y / CELL;
    const p1 = Math.random() * 6.28;
    const p2 = Math.random() * 6.28;
    const p3 = Math.random() * 6.28;
    const reach = rad * (1 + rough * 1.6) + 1;
    const y0 = Math.max(0, Math.floor(cy - reach));
    const y1 = Math.min(this.GH - 1, Math.ceil(cy + reach));
    let n = 0;
    for (let row = y0; row <= y1; row++) {
      let run = -1;
      const x0 = Math.max(0, Math.floor(cx - reach));
      const x1 = Math.min(this.GW - 1, Math.ceil(cx + reach));
      for (let col = x0; col <= x1 + 1; col++) {
        let hit = false;
        if (col <= x1) {
          const dx = col + 0.5 - cx;
          const dy = row + 0.5 - cy;
          const d = Math.hypot(dx, dy);
          if (d <= reach) {
            const ang = Math.atan2(dy, dx);
            const noisy = rad * (1 + rough * (Math.sin(3 * ang + p1) * 0.6 + Math.sin(7 * ang + p2) * 0.4 + Math.sin(13 * ang + p3) * 0.25));
            hit = d <= noisy && !this.shields(col, row);
          }
        }
        if (hit) {
          if (this.take(col, row, on)) n++;
          if (run < 0) run = col;
        } else if (run >= 0) {
          this.pg.clearRect(run * this.pix, row * this.pix, (col - run) * this.pix, this.pix);
          this.tx.clearRect(run * this.pix, row * this.pix, (col - run) * this.pix, this.pix);
          run = -1;
        }
      }
    }
    return n;
  }

  /** Carve every cell inside the rectangle that `hit` accepts. */
  carveWhere(x0: number, y0: number, x1: number, y1: number, hit: (x: number, y: number) => boolean, on?: CarveCell) {
    const left = Math.max(0, Math.floor(x0));
    const right = Math.min(this.GW - 1, Math.ceil(x1));
    let n = 0;
    for (let y = Math.max(0, Math.floor(y0)); y <= Math.min(this.GH - 1, Math.ceil(y1)); y++) {
      let run = -1;
      for (let x = left; x <= right + 1; x++) {
        if (x <= right && hit(x, y) && !this.shields(x, y)) {
          if (this.take(x, y, on)) n++;
          if (run < 0) run = x;
        } else if (run >= 0) {
          this.pg.clearRect(run * this.pix, y * this.pix, (x - run) * this.pix, this.pix);
          this.tx.clearRect(run * this.pix, y * this.pix, (x - run) * this.pix, this.pix);
          run = -1;
        }
      }
    }
    return n;
  }

  scorch(x: number, y: number, inner: number, outer: number, strength = 1) {
    const cx = x / CELL;
    const cy = y / CELL;
    const r0 = inner / CELL;
    const r1 = outer / CELL;
    const bands = [0.22, 0.45, 0.7, 0.9];
    const dark: number[][] = [[], [], [], []];
    const pale: number[][] = [[], [], [], []];
    const soot = pack(28, 14, 9);
    for (let row = Math.max(0, Math.floor(cy - r1)); row <= Math.min(this.GH - 1, Math.ceil(cy + r1)); row++) {
      for (let col = Math.max(0, Math.floor(cx - r1)); col <= Math.min(this.GW - 1, Math.ceil(cx + r1)); col++) {
        const i = row * this.GW + col;
        if (this.mat[i] === EMPTY) continue;
        const d = Math.hypot(col + 0.5 - cx, row + 0.5 - cy);
        if (d < r0 - 1 || d > r1) continue;
        const t = Math.max(0, (d - r0) / (r1 - r0));
        const heat = strength * Math.pow(1 - t, 1.4) + (bayer(col, row) - 0.5) * 0.3;
        let band = -1;
        for (let b = bands.length - 1; b >= 0; b--) if (heat >= bands[b]) { band = b; break; }
        if (band < 0) continue;
        if (lum(this.col[i]) < 0.3) {
          if (band < 2) continue;
          pale[band].push(i);
          this.col[i] = mix(this.col[i], pack(196, 182, 190), bands[band] * 0.6);
        } else {
          dark[band].push(i);
          this.col[i] = mix(this.col[i], soot, bands[band]);
        }
      }
    }
    this.pg.save();
    this.pg.globalCompositeOperation = "source-atop";
    dark.forEach((cells, band) => {
      if (!cells.length) return;
      this.pg.fillStyle = `rgba(28,14,9,${bands[band]})`;
      for (const i of cells) this.pg.fillRect((i % this.GW) * this.pix, Math.floor(i / this.GW) * this.pix, this.pix, this.pix);
    });
    pale.forEach((cells, band) => {
      if (!cells.length) return;
      this.pg.fillStyle = `rgba(196,182,190,${bands[band] * 0.6})`;
      for (const i of cells) this.pg.fillRect((i % this.GW) * this.pix, Math.floor(i / this.GW) * this.pix, this.pix, this.pix);
    });
    this.pg.restore();
  }

  /** Cells still standing around the last carve, bucketed by Chebyshev distance. */
  rim(reach = 2): { cells: number[]; ring: number[] } {
    const found = new Map<number, number>();
    for (const cell of this.carved) {
      const x = cell % this.GW;
      const y = Math.floor(cell / this.GW);
      for (let dy = -reach; dy <= reach; dy++) {
        for (let dx = -reach; dx <= reach; dx++) {
          const nx = x + dx;
          const ny = y + dy;
          if (nx < 0 || ny < 0 || nx >= this.GW || ny >= this.GH) continue;
          const i = ny * this.GW + nx;
          if (this.mat[i] === EMPTY) continue;
          const dist = Math.max(Math.abs(dx), Math.abs(dy));
          const prev = found.get(i);
          if (prev === undefined || dist < prev) found.set(i, dist);
        }
      }
    }
    const cells: number[] = [];
    const ring: number[] = [];
    for (const [cell, dist] of found) {
      cells.push(cell);
      ring.push(dist);
    }
    return { cells, ring };
  }

  /** Darken a carved rim. `ring` is the distance band from `rim()`. */
  char(cells: number[], ring: number[]) {
    const bands = [0, 0.78, 0.5, 0.25];
    const soot = pack(28, 14, 9);
    this.pg.save();
    this.pg.globalCompositeOperation = "source-atop";
    for (let band = 1; band <= 3; band++) {
      this.pg.fillStyle = `rgba(26,12,8,${bands[band]})`;
      for (let i = 0; i < cells.length; i++) {
        if (ring[i] !== band) continue;
        const cell = cells[i];
        const x = cell % this.GW;
        const y = Math.floor(cell / this.GW);
        if (band === 3 && bayer(x, y) > 0.5) continue;
        this.pg.fillRect(x * this.pix, y * this.pix, this.pix, this.pix);
        this.col[cell] = mix(this.col[cell], soot, bands[band]);
      }
    }
    this.pg.restore();
  }

  crack(index: number, x: number, y: number) {
    const block = this.blocks[index];
    if (!block) return;
    const x0 = Math.floor(block.x / CELL);
    const y0 = Math.floor(block.y / CELL);
    const x1 = Math.floor((block.x + block.w) / CELL);
    const y1 = Math.floor((block.y + block.h) / CELL);
    const cells: number[] = [];
    const walk = (cx: number, cy: number, ang: number, len: number) => {
      for (let i = 0; i < len; i++) {
        ang += (Math.random() - 0.5) * 0.64;
        cx += Math.round(Math.cos(ang));
        cy += Math.round(Math.sin(ang));
        if (cx <= x0 || cy <= y0 || cx >= x1 || cy >= y1) return;
        const cell = cy * this.GW + cx;
        if (this.mat[cell] !== SOLID || this.owner[cell] !== index) return;
        cells.push(cell);
      }
    };
    const n = 2 + Math.floor(Math.random() * 2);
    for (let i = 0; i < n; i++) walk(Math.floor(x / CELL), Math.floor(y / CELL), Math.random() * Math.PI * 2, 8 + Math.floor(Math.random() * 14));
    if (!cells.length) return;
    for (const cell of cells) {
      if (this.crackSet.has(cell)) continue;
      this.crackSet.add(cell);
      this.cracks.push(cell);
    }
  }

  drawCracks(ctx: CanvasRenderingContext2D, camX: number, camY: number, scale: number) {
    if (!this.cracks.length) return;
    ctx.save();
    ctx.fillStyle = "rgba(14,8,20,0.5)";
    const size = CELL * scale;
    // Cells are unique, so one path fill paints exactly what a fillRect per cell did.
    ctx.beginPath();
    let live = 0;
    for (const cell of this.cracks) {
      if (this.mat[cell] === EMPTY) {
        this.crackSet.delete(cell);
        continue;
      }
      const x = cell % this.GW;
      const y = Math.floor(cell / this.GW);
      ctx.rect((x * CELL - camX) * scale, (y * CELL - camY) * scale, size, size);
      this.cracks[live++] = cell;
    }
    // Cleared cells never crack again: forget them so the list does not grow forever.
    this.cracks.length = live;
    ctx.fill();
    ctx.restore();
  }

  /** Darken one standing cell. Flame leaves this on the cells next to a burn. */
  charCell(i: number, amount: number) {
    if (i < 0 || i >= this.mat.length || this.mat[i] === EMPTY) return;
    const soot = pack(28, 14, 9);
    this.col[i] = mix(this.col[i], soot, amount);
    const x = i % this.GW;
    const y = Math.floor(i / this.GW);
    this.pg.save();
    this.pg.globalCompositeOperation = "source-atop";
    this.pg.fillStyle = `rgba(28,14,9,${amount.toFixed(2)})`;
    this.pg.fillRect(x * this.pix, y * this.pix, this.pix, this.pix);
    this.pg.restore();
  }

  clearCell(i: number) {
    this.applyClear(i, true);
  }

  /** Drop a cell to empty and erase it on the page canvas. Letters leave the text canvas on their own. `record` writes a CLEAR op. */
  applyClear(i: number, record: boolean) {
    if (i < 0 || i >= this.mat.length || this.mat[i] === EMPTY) return;
    if (this.shields(i % this.GW, Math.floor(i / this.GW))) return;
    const owner = this.owner[i];
    this.mat[i] = EMPTY;
    this.owner[i] = -1;
    if (owner >= 0 && this.blocks[owner]) this.blocks[owner].lost++;
    this.removedCells++;
    if (record) this.log?.clear(i);
    const x = i % this.GW;
    const y = Math.floor(i / this.GW);
    this.pg.clearRect(x * this.pix, y * this.pix, this.pix, this.pix);
    this.tx.clearRect(x * this.pix, y * this.pix, this.pix, this.pix);
  }

  bakeIndex(i: number, color: number, record: boolean) {
    const x = i % this.GW;
    const y = Math.floor(i / this.GW);
    this.bakeDebris(x, y, color, record);
  }

  /** Grid-only debris. The sprite blit is the picture; the log is for other clients. */
  markDebris(x: number, y: number, color: number) {
    if (x < 0 || y < 0 || x >= this.GW || y >= this.GH) return;
    const i = y * this.GW + x;
    if (this.mat[i] >= SOLID) return;
    this.mat[i] = DEBRIS;
    this.col[i] = color >>> 0;
    this.owner[i] = -1;
    this.log?.debris(i, color >>> 0);
  }

  bakeDebris(x: number, y: number, color: number, record = true) {
    this.markDebris(x, y, color);
    if (record) return;
    this.pg.fillStyle = css(color);
    this.pg.fillRect(x * this.pix, y * this.pix, this.pix, this.pix);
  }

  /**
   * `dirX`/`dirY` are a unit shove. When `blastX` is set the slab is thrown
   * away from that point, which is how explosions lift a header from one edge.
   */
  damageBlock(index: number, amount: number, dirX: number, dirY: number, blastX?: number, blastY?: number, hitX?: number, hitY?: number) {
    const block = this.blocks[index];
    if (!block?.alive) return;
    const before = block.damage / block.hp;
    block.damage += amount;
    block.shake = Math.min(1, block.shake + 0.4);
    const after = block.damage / block.hp;
    const px = Math.max(block.x + 3, Math.min(block.x + block.w - 3, hitX ?? blastX ?? block.x + block.w / 2));
    const py = Math.max(block.y + 3, Math.min(block.y + block.h - 3, hitY ?? blastY ?? block.y + block.h / 2));
    for (const [step, mark] of [0.3, 0.55, 0.8].entries()) {
      if (before < mark && after >= mark) {
        block.cracked = step + 1;
        this.crack(index, px, py);
      }
    }
    if (!block.canDetach || (block.damage < block.hp && block.lost < block.cells * 0.38)) return;
    const area = Math.max(1, block.w * block.h);
    const impulse = Math.max(15, Math.min(380, 2600 / Math.sqrt(area)));
    let vx = dirX * impulse;
    let vy = dirY * impulse - 40;
    if (blastX !== undefined && blastY !== undefined) {
      const cx = block.x + block.w / 2;
      const cy = block.y + block.h / 2;
      const dist = Math.hypot(cx - blastX, cy - blastY) || 1;
      vx = ((cx - blastX) / dist) * impulse * 1.4;
      vy = ((cy - blastY) / dist) * impulse * 1.4 - 120;
    }
    const spin = (Math.random() * 3 - 1.5) * Math.min(3, 300 / Math.sqrt(area));
    if (this.breaker) this.breaker(index, vx, vy, spin);
    else this.detach(index, vx, vy, spin);
  }

  /** Cells still standing in a block plus everything a detach would carry with it. */
  subtreeCells(index: number): number {
    const stack = [index];
    const seen = new Set<number>();
    let n = 0;
    while (stack.length) {
      const i = stack.pop()!;
      if (seen.has(i)) continue;
      seen.add(i);
      const b = this.blocks[i];
      if (!b?.alive) continue;
      n += Math.max(0, b.cells - b.lost);
      for (const c of b.children) stack.push(c);
    }
    return n;
  }

  detach(index: number, vx: number, vy: number, av?: number): { canvas: HTMLCanvasElement; x: number; y: number; w: number; h: number; av: number } | null {
    const block = this.blocks[index];
    if (!block?.alive || !block.canDetach) return null;
    const stack = [index];
    const seen = new Set<number>();
    let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
    while (stack.length) {
      const id = stack.pop()!;
      if (seen.has(id)) continue;
      seen.add(id);
      const b = this.blocks[id];
      if (!b?.alive) continue;
      x0 = Math.min(x0, b.x);
      y0 = Math.min(y0, b.y);
      x1 = Math.max(x1, b.x + b.w);
      y1 = Math.max(y1, b.y + b.h);
      for (const child of b.children) stack.push(child);
    }
    if (!Number.isFinite(x0)) return null;
    const spin = av ?? (Math.random() - 0.5) * 3;
    const canvas = mk(Math.max(1, (x1 - x0) * this.Z), Math.max(1, (y1 - y0) * this.Z));
    const g = canvas.getContext("2d")!;
    this.pageCache.copy(g, x0 * this.Z, y0 * this.Z, canvas.width, canvas.height, 0, 0, canvas.width, canvas.height);
    this.textCache.copy(g, x0 * this.Z, y0 * this.Z, canvas.width, canvas.height, 0, 0, canvas.width, canvas.height);
    const piece = { canvas, x: x0, y: y0, w: x1 - x0, h: y1 - y0, av: spin };
    for (const id of seen) {
      const b = this.blocks[id];
      b.alive = false;
      const c0 = Math.max(0, Math.floor(b.x / CELL));
      const r0 = Math.max(0, Math.floor(b.y / CELL));
      const c1 = Math.min(this.GW - 1, Math.ceil((b.x + b.w) / CELL));
      const r1 = Math.min(this.GH - 1, Math.ceil((b.y + b.h) / CELL));
      for (let y = r0; y <= r1; y++) {
        for (let x = c0; x <= c1; x++) {
          const i = y * this.GW + x;
          if (this.owner[i] !== id || this.shields(x, y)) continue;
          this.mat[i] = EMPTY;
          this.owner[i] = -1;
          this.removedCells++;
          this.pg.clearRect(x * this.pix, y * this.pix, this.pix, this.pix);
        }
      }
    }
    if (av === undefined) this.log?.block(index, vx, vy, spin);
    return piece;
  }
}

export type { Rect };
